import React, { useEffect, useCallback, useState } from "react";

import {
  Box,
  Container,
  Grid,
} from "@mui/material";

import ThreeSlotWorking from "./components/ThreeSlotWorking";
import ThreeSlotHeroBanner from "./components/ThreeSlotHeroBanner";

import { CommonBreadcrumbs } from "../../../Components/ui";
import { Path, DEFAULT_CURRENCY } from "../../../../static/Constants";
import { THREE_SLOT_CONFIG } from "./components/threeSlotConfig";
import { colors } from "../../../../theme";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useHistory } from "react-router-dom";

import NavBar from "../../../Components/NavBar";

import {
  getConnectedAccount,
  connectWallet as triggerConnectWallet,
  subscribeToWalletEvents,
} from "../../../../static/walletUtils";
import slotInstance from "../../../Service/slotInstance";
import web3 from "../../../Service/web3";

import ThreeSlotMachineCard from "./components/ThreeSlotMachineCard";

import "./ThreeSlotLottery.css";


// Sample / on-chain 3-Slot Lotteries with Fiat and Crypto Pokies editions
export const DUMMY_LOTTERIES = [
  {
    lotteryId: 101,
    name: "3-SlotBET #101",
    creator: "AutoBET Official",
    slotCategory: "fiat",
    jackpots: {
      mini: {
        amount: "1364.22",
        seedValue: "1000.00",
        contributionPerSpin: "0.020",
      },
      major: {
        amount: "8945.76",
        seedValue: "5000.00",
        contributionPerSpin: "0.008",
      },
      mega: {
        amount: "54228.41",
        seedValue: "20000.00",
        contributionPerSpin: "0.002",
      },
    },
  }
];

const SlotLottery = () => {
  const history = useHistory();

  const [account, setAccount] = useState("");
  const [lotteries,setLotteries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSlotGames = useCallback(async () => {
    setLoading(true);

    try {
      if (!slotInstance?.methods?.getOngoingGames) {
        console.warn("getOngoingGames method not found on slotInstance");
        setLotteries([]);
        return;
      }

      const ongoingGames = await slotInstance.methods
          .getOngoingGames()
          .call();

      console.log("Raw ongoing slot games:", ongoingGames);

      const mappedLotteries = ongoingGames
          .filter((game) => Number(game.reels || game[1]) === 3 && Boolean(game.active ?? game[4]))
          .map((game, index) => {
            const gameId = game.gameId || game[0] || index + 1;
            const reels = game.reels || game[1];
            const minBet = game.minBet || game[2] || "0";
            const maxBet = game.maxBet || game[3] || "0";
            const active = game.active ?? game[4];
            const startTime = game.startTime || game[5] || "0";
            const endTime = game.endTime || game[6] || "0";
            const owner = game.owner || game[7];

            const minBetFormatted = web3?.utils?.fromWei
                ? web3.utils.fromWei(String(minBet), "ether")
                : String(minBet);

            const maxBetFormatted = web3?.utils?.fromWei
                ? web3.utils.fromWei(String(maxBet), "ether")
                : String(maxBet);

            const numericMin = Number(minBetFormatted) || 0;
            const numericMax = Number(maxBetFormatted) || 0;

            return {
              lotteryId: Number(gameId),
              gameId: Number(gameId),
              name: `3-SlotBET #${gameId}`,
              creator: owner
                  ? `${owner.slice(0, 6)}...${owner.slice(-4)}`
                  : "AutoBET Official",
              owner,
              reels: Number(reels),
              active,
              startTime,
              endTime,
              minBet: minBetFormatted,
              maxBet: maxBetFormatted,
              slotCategory: "crypto",
              jackpots: {
                mini: {
                  amount: (numericMax * 0.15).toFixed(2),
                  seedValue: (numericMax * 0.1).toFixed(2),
                  contributionPerSpin: (numericMin * 0.02).toFixed(3),
                },
                major: {
                  amount: (numericMax * 0.45).toFixed(2),
                  seedValue: (numericMax * 0.3).toFixed(2),
                  contributionPerSpin: (numericMin * 0.008).toFixed(3),
                },
                mega: {
                  amount: (numericMax * 1.2).toFixed(2),
                  seedValue: (numericMax * 0.6).toFixed(2),
                  contributionPerSpin: (numericMin * 0.002).toFixed(3),
                },
              },
            };
          });

      console.log("Mapped 3-slot games:", mappedLotteries);

      setLotteries(mappedLotteries);
    } catch (error) {
      console.log(error);
      toast.error("Unable to load 3-SlotBET games.");
      setLotteries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const combinedPrize = lotteries.reduce((acc, lottery) => {
    return (
      acc +
      parseFloat(lottery.jackpots.mini.amount) +
      parseFloat(lottery.jackpots.major.amount) +
      parseFloat(lottery.jackpots.mega.amount)
    );
  }, 0).toFixed(2);

  const checkAccount = useCallback(async () => {
    const acc = await getConnectedAccount();
    setAccount(acc);
  }, []);

  const connectWallet = async () => {
    const res = await triggerConnectWallet();

    if (res.account) {
      setAccount(res.account);
    } else if (res.error === "NO_WALLET") {
      toast.error(res.message);
    } else if (res.isPending) {
      toast.info(res.message);
    } else if (!res.isCancelled && res.message) {
      toast.error(res.message);
    }
  };

  const handleOpenLottery = (lotteryId) => {
    history.push(`/three-slotlottery/${lotteryId}`);
  };

  useEffect(() => {
    checkAccount();
    fetchSlotGames();

    const unsubscribe = subscribeToWalletEvents({
      onAccountsChanged: (acc) => {
        setAccount(acc);
      },
      onDisconnect: () => setAccount(""),
    });

    return unsubscribe;
  }, [checkAccount, fetchSlotGames]);

  return (
    <Box
      sx={{
        position: "relative",
        overflowX: "hidden",
        minHeight: "100vh",
        bgcolor: colors.dashboard.canvas,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={3000}
        theme="colored"
      />

      <NavBar />

      <Container
        maxWidth="xl"
        className="slot-page-container"
        sx={{
          pt: { xs: "96px", sm: "108px", md: "116px" },
          pb: 8,
          flexGrow: 1,
        }}
      >
        <Box sx={{ mb: 4 }}>
          <CommonBreadcrumbs
            items={[
              { label: "Home", path: Path.root },
              { label: "All Lotteries", path: Path.allLotteries },
              { label: `${THREE_SLOT_CONFIG.name}` },
            ]}
          />
        </Box>

        <ThreeSlotHeroBanner
          totalActive={lotteries.length}
          combinedPrize={combinedPrize}
          currency={DEFAULT_CURRENCY}
        />

        <Grid container spacing={3} alignItems="flex-start">
          {lotteries.map((lottery) => (
            <Grid item xs={12} sm={6} lg={4} key={lottery.lotteryId}>
              <Box
                onClick={() => handleOpenLottery(lottery.lotteryId)}
                sx={{
                  cursor: "pointer",
                }}
              >
                <ThreeSlotMachineCard
                  lotteryData={lottery}
                  account={account}
                  onConnectWallet={connectWallet}
                />
              </Box>
            </Grid>
          ))}
        </Grid>

        <ThreeSlotWorking />
      </Container>
    </Box>
  );
};

export default SlotLottery;